/**
 * Comstar billing export — client side.
 *
 * Sprint L. Closes the audit gap 'Comstar export not wired'. Comstar
 * is one example billing partner; agencies hand their partner HCPCS +
 * modifier + ICD-10 + mileage + signed-PCS data via either:
 *
 *   - csv             — POST a CSV upload to a configured endpoint
 *   - 837p            — EDI X12 837P (placeholder — full encoder
 *                       lands when an agency provides the spec)
 *   - portal_upload   — generate the file; operator uploads
 *                       manually to the Comstar portal
 *   - manual_download — same as portal_upload, but no preference
 *                       is set yet (default until /admin/comstar-
 *                       config is filled in)
 *
 * The unknowns the agency provides via /admin/comstar-config:
 *   - endpoint URL (for csv / 837p formats)
 *   - auth method + secret (bearer, basic, x-api-key header, none)
 *   - column-name overrides (field_map jsonb)
 *
 * Until those are filled in, every export run produces a CSV file
 * that downloads to the operator's device with a comstar_exports row
 * stamped status='manual_download' so the audit trail still exists.
 *
 * Hard gates ENFORCED here (not in UI alone):
 *   1. Any blocker-severity denial check failing → refuse to export
 *   2. PCS expired (cmn_valid_through < today) → refuse to export
 *      for non-emergency runs that need a PCS
 *
 * Tested call site: BillingTab → onExport handler.
 */

import { supabase } from './supabase';
import type { EPCR } from '@/types';
import type { BillingBundle } from './billing-catalog';

export interface ComstarConfig {
  agency_id: string;
  format: 'csv' | '837p' | 'portal_upload' | 'manual_download';
  endpoint_url: string | null;
  auth_method: 'none' | 'bearer' | 'basic' | 'api_key_header';
  auth_secret_ref: string | null;
  auth_header_name: string | null;
  field_map: Record<string, string>;
  contact_name: string | null;
  contact_email: string | null;
  notes: string | null;
}

export interface ComstarExportResult {
  status: 'manual_download' | 'sandbox' | 'sent' | 'failed' | 'rejected';
  exportId: string;
  csv?: string;
  filename?: string;
  errors?: string[];
}

/**
 * Canonical CSV column set. Order matches the column names below.
 * Override a column name (not the order) via comstar_agency_config
 * .field_map = { hcpcs_base: 'HCPCS_CODE_1', ... }.
 */
export const COMSTAR_DEFAULT_COLUMNS = [
  'export_id',         // our uuid for the export attempt
  'epcr_id',
  'run_number',
  'patient_last_name',
  'patient_first_name',
  'patient_dob',
  'patient_gender',
  'patient_address',
  'patient_city',
  'patient_state',
  'patient_zip',
  'insurance_carrier',
  'insurance_policy',
  'incident_date',
  'incident_address',
  'incident_city',
  'hcpcs_base',
  'hcpcs_mileage',
  'loaded_miles',
  'modifier',
  'level_of_service',
  'emergency',
  'icd10_primary',
  'icd10_secondary',
  'pcs_signed_date',
  'pcs_valid_through',
  'pcs_physician_name',
  'pcs_physician_npi',
  'pcs_annual_standing',
  'destination',
] as const;

export type ComstarColumn = typeof COMSTAR_DEFAULT_COLUMNS[number];

function devMode(): boolean {
  return !import.meta.env.VITE_SUPABASE_URL;
}

/**
 * Load the agency's Comstar config. Falls back to a 'manual_download'
 * default when no row exists yet so the export pipeline still works
 * (operator just gets a CSV download instead of an HTTP POST).
 */
export async function loadComstarConfig(agencyId: string): Promise<ComstarConfig> {
  const fallback: ComstarConfig = {
    agency_id: agencyId,
    format: 'manual_download',
    endpoint_url: null,
    auth_method: 'none',
    auth_secret_ref: null,
    auth_header_name: null,
    field_map: {},
    contact_name: null,
    contact_email: null,
    notes: null,
  };
  if (devMode()) return fallback;
  const { data } = await supabase
    .from('comstar_agency_config')
    .select('*')
    .eq('agency_id', agencyId)
    .maybeSingle();
  return (data as ComstarConfig | null) ?? fallback;
}

export async function saveComstarConfig(cfg: ComstarConfig, currentUserId: string | null): Promise<void> {
  if (devMode()) return;
  await supabase.from('comstar_agency_config').upsert({
    ...cfg,
    updated_by: currentUserId,
  });
}

/**
 * Project EPCR + BillingBundle onto a flat Comstar row keyed by
 * canonical field ids. Caller maps these to columns using
 * cfg.field_map.
 */
export function buildComstarRow(args: {
  epcr: EPCR;
  bundle: BillingBundle;
  exportId: string;
  incidentRunNumber: string | null;
  incidentDate: string | null;
  incidentAddress: string | null;
  incidentCity: string | null;
}): Record<ComstarColumn, string> {
  const { epcr, bundle, exportId } = args;
  return {
    export_id:           exportId,
    epcr_id:             epcr.id,
    run_number:          args.incidentRunNumber ?? '',
    patient_last_name:   epcr.patient_last_name ?? '',
    patient_first_name:  epcr.patient_first_name ?? '',
    patient_dob:         epcr.patient_dob ?? '',
    patient_gender:      epcr.patient_gender ?? '',
    patient_address:     epcr.patient_address ?? '',
    patient_city:        epcr.patient_city ?? '',
    patient_state:       epcr.patient_state ?? '',
    patient_zip:         epcr.patient_zip ?? '',
    insurance_carrier:   epcr.insurance_carrier ?? '',
    insurance_policy:    epcr.insurance_policy ?? '',
    incident_date:       args.incidentDate ?? '',
    incident_address:    args.incidentAddress ?? '',
    incident_city:       args.incidentCity ?? '',
    hcpcs_base:          bundle.baseHcpcs,
    hcpcs_mileage:       bundle.mileageHcpcs ?? '',
    loaded_miles:        bundle.loadedMiles != null ? String(bundle.loadedMiles) : '',
    modifier:            bundle.combinedModifier ?? '',
    level_of_service:    bundle.levelOfService,
    emergency:           bundle.emergency ? 'Y' : 'N',
    icd10_primary:       epcr.primary_impression ?? '',          // Comstar maps the impression text to ICD-10 their side
    icd10_secondary:     (epcr.secondary_impressions ?? []).join('|'),
    pcs_signed_date:     epcr.cmn_signed_date ?? '',
    pcs_valid_through:   epcr.cmn_valid_through ?? '',
    pcs_physician_name:  epcr.cmn_physician_name ?? '',
    pcs_physician_npi:   epcr.cmn_physician_npi ?? '',
    pcs_annual_standing: epcr.cmn_is_annual_standing ? 'Y' : 'N',
    destination:         (epcr as unknown as { destination_hospital?: string | null }).destination_hospital ?? '',
  };
}

/** RFC 4180 CSV escape — quote if contains comma, quote, or newline. */
function csvCell(v: string): string {
  if (v == null) return '';
  if (/[,"\n\r]/.test(v)) {
    return '"' + v.replace(/"/g, '""') + '"';
  }
  return v;
}

export function rowToCsv(
  row: Record<ComstarColumn, string>,
  fieldMap: Record<string, string> = {},
  includeHeader = true,
): string {
  const cols = COMSTAR_DEFAULT_COLUMNS.slice();
  const header = cols.map((c) => csvCell(fieldMap[c] ?? c)).join(',');
  const data = cols.map((c) => csvCell(row[c])).join(',');
  return includeHeader ? `${header}\n${data}` : data;
}

/* ============================================================
 * GATES — refuse to export when blocker checks fail or PCS expired
 * ============================================================ */

export interface ExportGateResult {
  ok: boolean;
  blockers: string[];
}

export function checkExportGates(args: {
  epcr: EPCR;
  bundle: BillingBundle;
  /** Whether the run is non-emergency and therefore requires a valid PCS. */
  pcsRequired: boolean;
}): ExportGateResult {
  const blockers: string[] = [];

  // Denial-check blockers (any failing check at severity='blocker').
  for (const c of args.bundle.denialChecks) {
    if (c.severity === 'blocker' && !c.ok) {
      blockers.push(`Denial pre-check failed: ${c.label}${c.detail ? ` — ${c.detail}` : ''}.`);
    }
  }

  // PCS expiry — non-emergency only.
  if (args.pcsRequired) {
    const validThrough = args.epcr.cmn_valid_through;
    if (!validThrough) {
      blockers.push('PCS / CMN required for non-emergency billing but no signed date is on file.');
    } else {
      const today = new Date().toISOString().slice(0, 10);
      if (validThrough < today) {
        blockers.push(`PCS expired ${validThrough}. Renew the Physician Certification Statement before exporting (CMS 42 CFR §410.40(d)).`);
      }
    }
  }

  return { ok: blockers.length === 0, blockers };
}

/* ============================================================
 * EXPORT — entry point called from BillingTab
 * ============================================================ */

export async function exportToComstar(args: {
  epcr: EPCR;
  bundle: BillingBundle;
  pcsRequired: boolean;
  agencyId: string;
  currentUserId: string | null;
  incidentRunNumber: string | null;
  incidentDate: string | null;
  incidentAddress: string | null;
  incidentCity: string | null;
}): Promise<ComstarExportResult> {
  const exportId = (typeof crypto !== 'undefined' && 'randomUUID' in crypto) ? crypto.randomUUID() : `${Date.now()}`;

  // Hard gates BEFORE anything else.
  const gate = checkExportGates({ epcr: args.epcr, bundle: args.bundle, pcsRequired: args.pcsRequired });
  if (!gate.ok) {
    return { status: 'failed', exportId, errors: gate.blockers };
  }

  const cfg = await loadComstarConfig(args.agencyId);
  const row = buildComstarRow({ ...args, exportId });
  const csv = rowToCsv(row, cfg.field_map);

  // Sandbox / manual_download path — no transmission, just download.
  if (cfg.format === 'manual_download' || cfg.format === 'portal_upload') {
    triggerCsvDownload(csv, `comstar-${args.incidentRunNumber ?? args.epcr.id}.csv`);
    if (!devMode()) {
      await supabase.from('comstar_exports').insert({
        id: exportId,
        agency_id: args.agencyId,
        epcr_id: args.epcr.id,
        exported_by: args.currentUserId,
        status: 'manual_download',
        format: cfg.format,
        endpoint_url: null,
        csv_payload: csv,
      });
    }
    return { status: 'manual_download', exportId, csv, filename: `comstar-${args.incidentRunNumber ?? args.epcr.id}.csv` };
  }

  // 837P — placeholder until the agency provides the EDI mapping.
  if (cfg.format === '837p') {
    if (!devMode()) {
      await supabase.from('comstar_exports').insert({
        id: exportId,
        agency_id: args.agencyId,
        epcr_id: args.epcr.id,
        exported_by: args.currentUserId,
        status: 'sandbox',
        format: '837p',
        endpoint_url: cfg.endpoint_url,
        csv_payload: csv,
        response_body: '837P encoder pending — using CSV payload as placeholder.',
      });
    }
    return { status: 'sandbox', exportId, csv, errors: ['837P encoder pending; CSV payload logged for review.'] };
  }

  // CSV format with endpoint configured — POST.
  if (!cfg.endpoint_url) {
    return { status: 'failed', exportId, errors: ['Comstar endpoint URL not configured (admin/comstar-config).'] };
  }

  // Network call. Auth is whatever the agency configured.
  // For now, the secret value isn't fetched from Vault on the client
  // (would expose it); send via a future edge function. Until that
  // function lands, treat 'csv with endpoint' the same as
  // manual_download but log the attempt for the admin to see.
  if (!devMode()) {
    await supabase.from('comstar_exports').insert({
      id: exportId,
      agency_id: args.agencyId,
      epcr_id: args.epcr.id,
      exported_by: args.currentUserId,
      status: 'sandbox',
      format: 'csv',
      endpoint_url: cfg.endpoint_url,
      csv_payload: csv,
      response_body: 'Direct POST awaiting comstar-export edge function. Payload logged for review.',
    });
  }
  triggerCsvDownload(csv, `comstar-${args.incidentRunNumber ?? args.epcr.id}.csv`);
  return { status: 'sandbox', exportId, csv };
}

function triggerCsvDownload(csv: string, filename: string) {
  if (typeof document === 'undefined') return;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}
